import React, { useState, useEffect, useMemo } from 'react';
import {
  Eye,
  Search,
  Loader,
  ChevronLeft,
  ChevronRight,
  X,
  SlidersHorizontal,
  ArrowUpDown,
} from 'lucide-react';
import Sidebar from '../../Layout/sideBar';
import axios from 'axios';
import { toast } from 'react-toastify';

const API_BASE_URL = 'https://api-backend-samah.vercel.app/api/pdf';
const PAGE_SIZE_OPTIONS = [6, 12, 24, 48];

const DATE_RANGES = [
  { value: 'all', label: 'All time' },
  { value: '7d', label: 'Last 7 days' },
  { value: '30d', label: 'Last 30 days' },
  { value: 'ytd', label: 'This year' },
];

const SORT_OPTIONS = [
  { value: 'newest', label: 'Newest first' },
  { value: 'oldest', label: 'Oldest first' },
  { value: 'name_asc', label: 'Name A–Z' },
  { value: 'name_desc', label: 'Name Z–A' },
];

const DEFAULT_FILTERS = {
  query: '',
  status: 'All',
  type: 'All',
  dateRange: 'all',
  sort: 'newest',
  pageSize: 6,
};

const FormsPage = () => {
  const [applications, setApplications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedForm, setSelectedForm] = useState(null);
  const [showPreview, setShowPreview] = useState(false);

  const [filters, setFilters] = useState(DEFAULT_FILTERS);
  const [currentPage, setCurrentPage] = useState(1);

  useEffect(() => {
    fetchApplications();
  }, []);

  const fetchApplications = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API_BASE_URL}/forms`);
      const formsArray = response.data?.data || [];
      setApplications(formsArray);
    } catch (err) {
      console.error('Error fetching applications:', err);
      const errorMsg = err.response?.data?.message || err.message;
      setError(errorMsg);
      toast.error(`Failed to load forms: ${errorMsg}`);
    } finally {
      setLoading(false);
    }
  };

  const transformToFormFormat = (app) => {
    const formData = app.form_data || {};

    const fields = [];
    if (formData.employeeName) {
      fields.push({ id: 'employeeName', label: 'Employee Name', type: 'text', value: formData.employeeName });
    }
    if (formData.dob) {
      fields.push({ id: 'dob', label: 'Date of Birth', type: 'date', value: formData.dob });
    }
    if (formData.gender) {
      fields.push({ id: 'gender', label: 'Gender', type: 'text', value: formData.gender });
    }
    if (formData.address) {
      fields.push({ id: 'address', label: 'Address', type: 'text', value: formData.address });
    }
    if (formData.email) {
      fields.push({ id: 'email', label: 'Email', type: 'email', value: formData.email });
    }
    if (formData.employeePhone) {
      fields.push({ id: 'phone', label: 'Phone', type: 'tel', value: formData.employeePhone });
    }
    if (formData.positionDesired) {
      fields.push({ id: 'position', label: 'Position Desired', type: 'text', value: formData.positionDesired });
    }
    if (formData.employmentType) {
      fields.push({ id: 'employmentType', label: 'Employment Type', type: 'text', value: formData.employmentType });
    }
    if (formData.startDate) {
      fields.push({ id: 'startDate', label: 'Start Date', type: 'date', value: formData.startDate });
    }

    let displayStatus = 'Draft';
    if (app.status === 'hr_reviewed') displayStatus = 'Active';
    else if (app.status === 'finalized') displayStatus = 'Finalized';
    else if (app.status === 'draft') displayStatus = 'Draft';

    const employeeName =
      formData.employeeName || formData.firstName || 'Unknown';

    return {
      id: app.id,
      slug_token: app.slug_token,
      employeeName,
      email: formData.email || '',
      title: `Application: ${employeeName}`,
      description: `Submitted on ${new Date(app.created_at).toLocaleDateString()}`,
      category: formData.employmentType || 'General',
      status: displayStatus,
      createdAt: new Date(app.created_at).getTime(),
      lastUpdated: new Date(app.updated_at).toLocaleDateString(),
      fields,
      rawData: app,
    };
  };

  const transformedForms = useMemo(
    () => applications.map(transformToFormFormat),
    [applications]
  );

  const statuses = useMemo(
    () => ['All', ...Array.from(new Set(transformedForms.map((f) => f.status))).sort()],
    [transformedForms]
  );

  const types = useMemo(
    () => ['All', ...Array.from(new Set(transformedForms.map((f) => f.category))).sort()],
    [transformedForms]
  );

  const filteredForms = useMemo(() => {
    const q = filters.query.trim().toLowerCase();

    let result = transformedForms.filter((form) => {
      const matchesQuery =
        !q ||
        form.employeeName.toLowerCase().includes(q) ||
        form.email.toLowerCase().includes(q) ||
        form.title.toLowerCase().includes(q);

      const matchesStatus =
        filters.status === 'All' || form.status === filters.status;

      const matchesType =
        filters.type === 'All' || form.category === filters.type;

      let matchesDate = true;
      if (filters.dateRange !== 'all') {
        const now = Date.now();
        const day = 24 * 60 * 60 * 1000;
        let cutoff;
        if (filters.dateRange === '7d') cutoff = now - 7 * day;
        else if (filters.dateRange === '30d') cutoff = now - 30 * day;
        else if (filters.dateRange === 'ytd') {
          const start = new Date(new Date().getFullYear(), 0, 1);
          cutoff = start.getTime();
        }
        matchesDate = form.createdAt >= cutoff;
      }

      return matchesQuery && matchesStatus && matchesType && matchesDate;
    });

    switch (filters.sort) {
      case 'oldest':
        result = [...result].sort((a, b) => a.createdAt - b.createdAt);
        break;
      case 'name_asc':
        result = [...result].sort((a, b) =>
          a.employeeName.localeCompare(b.employeeName)
        );
        break;
      case 'name_desc':
        result = [...result].sort((a, b) =>
          b.employeeName.localeCompare(a.employeeName)
        );
        break;
      case 'newest':
      default:
        result = [...result].sort((a, b) => b.createdAt - a.createdAt);
    }

    return result;
  }, [transformedForms, filters]);

  const totalItems = filteredForms.length;
  const totalPages = Math.max(1, Math.ceil(totalItems / filters.pageSize));
  const safePage = Math.min(currentPage, totalPages);

  const paginatedForms = useMemo(() => {
    const start = (safePage - 1) * filters.pageSize;
    return filteredForms.slice(start, start + filters.pageSize);
  }, [filteredForms, safePage, filters.pageSize]);

  const rangeStart = totalItems === 0 ? 0 : (safePage - 1) * filters.pageSize + 1;
  const rangeEnd = Math.min(safePage * filters.pageSize, totalItems);

  useEffect(() => {
    setCurrentPage(1);
  }, [filters]);

  useEffect(() => {
    if (currentPage > totalPages) setCurrentPage(totalPages);
  }, [currentPage, totalPages]);

  const setFilter = (key, value) =>
    setFilters((f) => ({ ...f, [key]: value }));

  const resetFilters = () => setFilters(DEFAULT_FILTERS);

  const hasActiveFilters =
    filters.query !== '' ||
    filters.status !== 'All' ||
    filters.type !== 'All' ||
    filters.dateRange !== 'all' ||
    filters.sort !== 'newest';

  const handlePreview = (form) => {
    setSelectedForm(form);
    setShowPreview(true);
  };

  const getStatusBadge = (status) => {
    const colors = {
      Active: 'bg-green-100 text-green-800',
      Draft: 'bg-yellow-100 text-yellow-800',
      Finalized: 'bg-blue-100 text-blue-800',
      Archived: 'bg-gray-100 text-gray-800',
    };
    return (
      <span
        className={`px-2 py-1 text-xs font-medium rounded-full ${
          colors[status] || 'bg-gray-100 text-gray-800'
        }`}
      >
        {status}
      </span>
    );
  };

  const PreviewModal = ({ form, onClose }) => (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto">
        <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex justify-between items-center">
          <div>
            <h2 className="text-xl font-bold text-gray-900">{form.title}</h2>
            <p className="text-sm text-gray-600 mt-1">{form.description}</p>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 text-2xl"
          >
            &times;
          </button>
        </div>

        <div className="p-6">
          <div className="space-y-6">
            {form.fields.length > 0 ? (
              form.fields.map((field) => (
                <div
                  key={field.id}
                  className="border-b border-gray-100 pb-4 last:border-0 last:pb-0"
                >
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    {field.label}
                  </label>
                  <div className="w-full px-3 py-2 bg-gray-50 border border-gray-200 rounded-lg text-gray-700">
                    {field.value || 'Not provided'}
                  </div>
                </div>
              ))
            ) : (
              <div className="text-center py-8 text-gray-500">
                No form fields available
              </div>
            )}
          </div>

          <div className="mt-6 pt-4 border-t border-gray-200">
            <button
              onClick={() => {
                window.location.href = `/hr/form/${form.slug_token}`;
              }}
              className="w-full px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
            >
              View Full Application
            </button>
          </div>
        </div>
      </div>
    </div>
  );

  if (loading) {
    return (
      <div className="flex min-h-screen bg-gray-50">
        <Sidebar />
        <div className="flex-1 ml-64 overflow-auto">
          <div className="p-8 flex items-center justify-center min-h-screen">
            <div className="text-center">
              <Loader className="w-8 h-8 animate-spin text-blue-600 mx-auto mb-4" />
              <p className="text-gray-600">Loading applications...</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex min-h-screen bg-gray-50">
        <Sidebar />
        <div className="flex-1 ml-64 overflow-auto">
          <div className="p-8 min-h-screen">
            <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded">
              Error loading applications: {error}
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex min-h-screen bg-gray-50">
      <Sidebar />

      <div className="flex-1 ml-64 overflow-auto">
        <div className="p-8 min-h-screen flex flex-col">
          <div className="mb-6">
            <h1 className="text-2xl font-bold text-gray-900">
              Employment Applications
            </h1>
            <p className="text-gray-600 mt-1">
              Browse and manage {applications.length} submitted applications
            </p>
          </div>

          {/* Filter bar */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 mb-4">
            <div className="flex items-center gap-2 mb-3 text-gray-700">
              <SlidersHorizontal className="w-4 h-4" />
              <span className="text-sm font-semibold">Filters</span>
              {hasActiveFilters && (
                <button
                  onClick={resetFilters}
                  className="ml-auto text-xs text-blue-600 hover:underline"
                >
                  Reset all
                </button>
              )}
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
              <div className="relative md:col-span-2 lg:col-span-3">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 w-5 h-5" />
                <input
                  type="text"
                  placeholder="Search by name, email, or title..."
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  value={filters.query}
                  onChange={(e) => setFilter('query', e.target.value)}
                />
              </div>

              <select
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                value={filters.status}
                onChange={(e) => setFilter('status', e.target.value)}
              >
                {statuses.map((status) => (
                  <option key={status} value={status}>
                    {status === 'All' ? 'All statuses' : status}
                  </option>
                ))}
              </select>

              <select
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                value={filters.type}
                onChange={(e) => setFilter('type', e.target.value)}
              >
                {types.map((type) => (
                  <option key={type} value={type}>
                    {type === 'All' ? 'All types' : type}
                  </option>
                ))}
              </select>

              <select
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                value={filters.dateRange}
                onChange={(e) => setFilter('dateRange', e.target.value)}
              >
                {DATE_RANGES.map((range) => (
                  <option key={range.value} value={range.value}>
                    {range.label}
                  </option>
                ))}
              </select>

              <div className="relative">
                <ArrowUpDown className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 w-4 h-4 pointer-events-none" />
                <select
                  className="w-full pl-9 pr-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  value={filters.sort}
                  onChange={(e) => setFilter('sort', e.target.value)}
                >
                  {SORT_OPTIONS.map((opt) => (
                    <option key={opt.value} value={opt.value}>
                      {opt.label}
                    </option>
                  ))}
                </select>
              </div>

              <select
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                value={filters.pageSize}
                onChange={(e) =>
                  setFilter('pageSize', Number(e.target.value))
                }
              >
                {PAGE_SIZE_OPTIONS.map((n) => (
                  <option key={n} value={n}>
                    {n} / page
                  </option>
                ))}
              </select>
            </div>

            {hasActiveFilters && (
              <div className="flex flex-wrap gap-2 mt-3 pt-3 border-t border-gray-100">
                {filters.status !== 'All' && (
                  <Chip
                    label={`Status: ${filters.status}`}
                    onRemove={() => setFilter('status', 'All')}
                  />
                )}
                {filters.type !== 'All' && (
                  <Chip
                    label={`Type: ${filters.type}`}
                    onRemove={() => setFilter('type', 'All')}
                  />
                )}
                {filters.dateRange !== 'all' && (
                  <Chip
                    label={`Date: ${
                      DATE_RANGES.find((r) => r.value === filters.dateRange)
                        ?.label
                    }`}
                    onRemove={() => setFilter('dateRange', 'all')}
                  />
                )}
                {filters.sort !== 'newest' && (
                  <Chip
                    label={`Sort: ${
                      SORT_OPTIONS.find((s) => s.value === filters.sort)?.label
                    }`}
                    onRemove={() => setFilter('sort', 'newest')}
                  />
                )}
                {filters.query && (
                  <Chip
                    label={`Search: "${filters.query}"`}
                    onRemove={() => setFilter('query', '')}
                  />
                )}
              </div>
            )}
          </div>

          {/* Result summary */}
          <div className="mb-4 text-sm text-gray-600">
            {totalItems === 0
              ? 'No applications match the current filters.'
              : `${totalItems} application${totalItems === 1 ? '' : 's'} found.`}
          </div>

          {/* Grid — fills remaining vertical space */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 flex-grow auto-rows-max">
            {paginatedForms.map((form) => (
              <div
                key={form.id}
                className="bg-white rounded-lg shadow-sm border border-gray-200 hover:shadow-md transition-shadow"
              >
                <div className="p-6">
                  <div className="flex justify-between items-start mb-2">
                    <h3 className="text-lg font-semibold text-gray-900 line-clamp-1">
                      {form.title}
                    </h3>
                    <span className="text-xs text-gray-500">
                      {form.fields.length} fields
                    </span>
                  </div>

                  <p className="text-gray-600 text-sm mb-4 line-clamp-2">
                    {form.description}
                  </p>

                  <div className="flex items-center justify-between mb-4">
                    <span className="text-xs text-gray-500">
                      Type: {form.category}
                    </span>
                    <span className="text-xs text-gray-500">
                      Updated: {form.lastUpdated}
                    </span>
                  </div>

                  <div className="flex items-center justify-between">
                    {getStatusBadge(form.status)}

                    <button
                      onClick={() => handlePreview(form)}
                      className="flex items-center px-3 py-1.5 bg-blue-50 text-blue-600 rounded-lg hover:bg-blue-100 transition-colors"
                    >
                      <Eye className="w-4 h-4 mr-1" />
                      Preview
                    </button>
                  </div>

                  <div className="mt-4 pt-4 border-t border-gray-100">
                    <button
                      onClick={() => {
                        window.location.href = `/hr/form/${form.slug_token}`;
                      }}
                      className="w-full px-4 py-2 bg-gray-50 text-gray-700 rounded-lg hover:bg-gray-100 transition-colors text-sm font-medium"
                    >
                      View Full Application
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {filteredForms.length === 0 && (
            <div className="text-center py-12 flex-grow">
              <p className="text-gray-500">
                {applications.length === 0
                  ? 'No applications found in the database'
                  : 'No applications match the current filters.'}
              </p>
              {applications.length > 0 && (
                <button
                  onClick={resetFilters}
                  className="mt-4 text-sm text-blue-600 hover:underline"
                >
                  Clear filters
                </button>
              )}
            </div>
          )}

          {/* Pagination — sits at bottom */}
          {filteredForms.length > 0 && (
            <div className="mt-auto pt-8">
              <div className="flex flex-col sm:flex-row items-center justify-between gap-4 bg-white rounded-lg shadow-sm border border-gray-200 px-4 py-3">
                <p className="text-sm text-gray-600">
                  Showing <span className="font-semibold">{rangeStart}</span>–
                  <span className="font-semibold">{rangeEnd}</span> of{' '}
                  <span className="font-semibold">{totalItems}</span>
                </p>

                <div className="flex items-center gap-1">
                  <button
                    onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                    disabled={safePage === 1}
                    className="inline-flex items-center gap-1 px-3 py-1.5 text-sm border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed"
                  >
                    <ChevronLeft className="w-4 h-4" />
                    Prev
                  </button>

                  {getPageNumbers(safePage, totalPages).map((page, i) =>
                    page === '...' ? (
                      <span
                        key={`ellipsis-${i}`}
                        className="px-2 text-gray-400 select-none"
                      >
                        …
                      </span>
                    ) : (
                      <button
                        key={page}
                        onClick={() => setCurrentPage(page)}
                        className={`min-w-[36px] px-2 py-1.5 text-sm rounded-lg border ${
                          page === safePage
                            ? 'bg-blue-600 text-white border-blue-600'
                            : 'border-gray-300 hover:bg-gray-50'
                        }`}
                      >
                        {page}
                      </button>
                    )
                  )}

                  <button
                    onClick={() =>
                      setCurrentPage((p) => Math.min(totalPages, p + 1))
                    }
                    disabled={safePage === totalPages}
                    className="inline-flex items-center gap-1 px-3 py-1.5 text-sm border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed"
                  >
                    Next
                    <ChevronRight className="w-4 h-4" />
                  </button>
                </div>
              </div>
            </div>
          )}

          {showPreview && selectedForm && (
            <PreviewModal
              form={selectedForm}
              onClose={() => {
                setShowPreview(false);
                setSelectedForm(null);
              }}
            />
          )}
        </div>
      </div>
    </div>
  );
};

function Chip({ label, onRemove }) {
  return (
    <span className="inline-flex items-center gap-1 rounded-md bg-blue-50 text-blue-700 px-2 py-1 text-xs font-medium">
      {label}
      <button
        onClick={onRemove}
        className="hover:bg-blue-100 rounded-full p-0.5"
        aria-label={`Remove filter: ${label}`}
      >
        <X className="w-3 h-3" />
      </button>
    </span>
  );
}

function getPageNumbers(current, total) {
  if (total <= 7) return Array.from({ length: total }, (_, i) => i + 1);
  if (current <= 4) return [1, 2, 3, 4, 5, '...', total];
  if (current >= total - 3)
    return [1, '...', total - 4, total - 3, total - 2, total - 1, total];
  return [1, '...', current - 1, current, current + 1, '...', total];
}

export default FormsPage;